import { Box, Button, TextField } from '@mui/material'
import { useTheme } from '@/contexts/ThemeContext'
import {
  createTextFieldStyles,
  createTextFieldErrorStyles,
  baseButtonStyles,
} from '@/components/shared/commonStyles'

interface MintBurnFormProps {
  recipient: string
  recipientError: string
  amount: string
  amountError: string
  onRecipientChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onMint: () => void
  onBurn: () => void
  isMinting: boolean
  isBurning: boolean
  isConfirmingMint: boolean
  isConfirmingBurn: boolean
  isSafeReady: boolean
}

export default function MintBurnForm({
  recipient,
  recipientError,
  amount,
  amountError,
  onRecipientChange,
  onAmountChange,
  onMint,
  onBurn,
  isMinting,
  isBurning,
  isConfirmingMint,
  isConfirmingBurn,
  isSafeReady,
}: MintBurnFormProps) {
  const { colors } = useTheme()

  const isMintDisabled =
    !amount ||
    !recipient ||
    !!recipientError ||
    parseFloat(amount) <= 0 ||
    !isSafeReady ||
    isConfirmingMint ||
    isMinting

  const isBurnDisabled =
    !amount ||
    parseFloat(amount) <= 0 ||
    !isSafeReady ||
    isConfirmingBurn ||
    isBurning

  const sharedButtonStyles = baseButtonStyles(colors)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <TextField
          label="Recipient Address (for mint)"
          value={recipient}
          onChange={onRecipientChange}
          placeholder="0x..."
          error={!!recipientError}
          helperText={recipientError}
          fullWidth
          sx={
            recipientError
              ? createTextFieldErrorStyles(colors)
              : createTextFieldStyles(colors)
          }
        />
        <TextField
          label="Amount"
          type="number"
          value={amount}
          onChange={onAmountChange}
          error={!!amountError}
          helperText={amountError}
          sx={
            amountError
              ? createTextFieldErrorStyles(colors)
              : createTextFieldStyles(colors)
          }
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
        }}
      >
        <Button
          onClick={onMint}
          variant="contained"
          disabled={isMintDisabled}
          sx={{
            ...sharedButtonStyles,
            backgroundColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: `${colors.primary}40`,
            },
          }}
        >
          {isConfirmingMint || isMinting ? 'Processing...' : 'Mint'}
        </Button>
        <Button
          onClick={onBurn}
          variant="contained"
          disabled={isBurnDisabled}
          sx={{
            ...sharedButtonStyles,
            backgroundColor: colors.text,
            '&:hover': {
              backgroundColor: colors.text,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: `${colors.text}40`,
            },
          }}
        >
          {isConfirmingBurn || isBurning ? 'Processing...' : 'Burn'}
        </Button>
      </Box>
    </Box>
  )
}
